import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { decorators, getPaginator } from '@orange-marmalade/paginate-this';
import { connect } from 'react-redux';
import { range, findWhere } from 'underscore';
import numeral from 'numeral';
import {
  Button,
  Col,
  Row,
  UncontrolledButtonDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem
} from 'reactstrap';
import { Link } from 'react-router-dom';
import URI from 'urijs';

export function Paginator(props, context) {
  const BS = context.theme;
  const {
    bottom,
    className,
    currentPage,
    totalPages,
    totalCount,
    hasPreviousPage,
    hasNextPage,
    sortFields = [],
    sort,
    sortReverse,
    top,
    children,
    isLoading,
    hidePage
  } = props;

  const nextSortDirection = sortReverse ? 'ASC' : 'DESC';

  return (
    <Row
      className={cx(
        className,
        BS.justifyContentCenter,
        BS.justifyContentSmStart,
        BS.alignItemsCenter,
        BS.colorGreyDark
      )}
    >
      {bottom && (
        <Col
          xs={12}
          className={cx(BS.colDividerSmPipe, BS.dSmNone, BS.textCenter, BS.pb3)}
        >
          <div className={`${BS.inputGroupGutter}`}>
            <Button
              tag={Link}
              color="dark-blue"
              disabled={!hasPreviousPage}
              to={URI(props.to)
                .search({
                  ...props.query,
                  page: currentPage - 1
                })
                .toString()}
            >
              Previous Page
            </Button>
            <Button
              tag={Link}
              color="dark-blue"
              disabled={!hasNextPage}
              to={URI(props.to)
                .search({
                  ...props.query,
                  page: currentPage + 1
                })
                .toString()}
            >
              Next Page
            </Button>
          </div>
        </Col>
      )}
      <Col xs={{ size: 'auto' }} className={cx(BS.mb3)}>
        {numeral(totalCount).format('0,0')} Results
      </Col>
      {!hidePage && (
        <Col
          xs={{ size: 'auto' }}
          className={cx(BS.colDividerPipe, BS.mb3, {
            [BS.dNone]: top,
            [BS.dSmBlock]: top
          })}
        >
          <div className={BS.inputGroupGutter}>
            {isLoading ? (
              <Button color="dark-blue" outline>
                Page {numeral(currentPage).format('0,0')}
              </Button>
            ) : (
              <UncontrolledButtonDropdown>
                <DropdownToggle color="dark-blue" outline>
                  Page {numeral(currentPage).format('0,0')}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem
                    tag={Link}
                    active={currentPage === 1}
                    to={URI(props.to)
                      .search({
                        ...props.query,
                        page: 1
                      })
                      .toString()}
                  >
                    First Page
                  </DropdownItem>
                  {currentPage > 1 &&
                    range(Math.max(2, currentPage - 8), currentPage).map(pageNum => {
                      return (
                        <DropdownItem
                          tag={Link}
                          key={pageNum}
                          to={URI(props.to)
                            .search({
                              ...props.query,
                              page: pageNum
                            })
                            .toString()}
                        >
                          Page {numeral(pageNum).format('0,0')}
                        </DropdownItem>
                      );
                    })}
                  {currentPage !== 1 && currentPage < totalPages && (
                    <DropdownItem
                      tag={Link}
                      active
                      to={URI(props.to)
                        .search({
                          ...props.query
                        })
                        .toString()}
                    >
                      Page {numeral(currentPage).format('0,0')}
                    </DropdownItem>
                  )}
                  {currentPage < totalPages &&
                    range(
                      currentPage + 1,
                      currentPage + Math.min(totalPages - currentPage, 8)
                    ).map(pageNum => {
                      return (
                        <DropdownItem
                          tag={Link}
                          key={pageNum}
                          to={URI(props.to)
                            .search({
                              ...props.query,
                              page: pageNum
                            })
                            .toString()}
                        >
                          Page {numeral(pageNum).format('0,0')}
                        </DropdownItem>
                      );
                    })}
                  {currentPage <= totalPages && totalPages !== 1 && (
                    <DropdownItem
                      tag={Link}
                      active={currentPage === totalPages}
                      to={URI(props.to)
                        .search({
                          ...props.query,
                          page: totalPages
                        })
                        .toString()}
                    >
                      Last Page
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            )}{' '}
            <span>of {numeral(totalPages).format('0,0')}</span>
          </div>
        </Col>
      )}
      {!hidePage && (
        <Col
          xs={{ size: 'auto' }}
          className={cx(BS.dNone, BS.dSmBlock, BS.colDividerPipe, BS.mb3)}
        >
          <div className={`${BS.inputGroupGutter}`}>
            <Button
              tag={Link}
              color="dark-blue"
              disabled={!hasPreviousPage}
              to={URI(props.to)
                .search({
                  ...props.query,
                  page: currentPage - 1
                })
                .toString()}
            >
              Previous Page
            </Button>
            <Button
              tag={Link}
              color="dark-blue"
              disabled={!hasNextPage}
              to={URI(props.to)
                .search({
                  ...props.query,
                  page: currentPage + 1
                })
                .toString()}
            >
              Next Page
            </Button>
          </div>
        </Col>
      )}
      {top && (
        <Col className={cx(BS.textRight)}>
          <div className={`${BS.inputGroupGutter}`}>
            {sortFields && sortFields.length > 0 && (
              <UncontrolledButtonDropdown className={cx(BS.mb3)}>
                <DropdownToggle color="dark-blue" outline caret>
                  {getCurrentSortLabel(props)}
                </DropdownToggle>
                <DropdownMenu right>
                  {sortFields.map((sortField, i) => {
                    const sortOrder = props.sortReverse ? 'DESC' : 'ASC';
                    const isActive =
                      sortField.id === sort && sortField.order === sortOrder;

                    return (
                      <DropdownItem
                        tag={Link}
                        key={i}
                        active={isActive}
                        to={URI(props.to)
                          .search({
                            ...props.query,
                            sort: sortField.id,
                            sortOrder:
                              sortField.order || (isActive ? nextSortDirection : 'ASC')
                          })
                          .toString()}
                      >
                        {sortField.name}
                      </DropdownItem>
                    );
                  })}
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            )}
            {children}
          </div>
        </Col>
      )}
    </Row>
  );
}

function getCurrentSortLabel(props) {
  var sortLabel = 'Sort Results';
  const sortOrder = props.sortReverse ? 'DESC' : 'ASC';

  const currentSort = findWhere(props.sortFields, {
    id: props.sort,
    order: sortOrder
  });

  if (currentSort) {
    sortLabel = currentSort.name;
  }

  return sortLabel;
}

Paginator.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  hasPreviousPage: PropTypes.bool,
  hasNextPage: PropTypes.bool
};

Paginator.contextTypes = {
  theme: PropTypes.object
};

export default decorators.paginateThis(
  connect((state, props) => ({
    totalCount: getPaginator(props.listId, state).get('totalCount')
  }))(Paginator)
);



// WEBPACK FOOTER //
// ./src/components/paginator/paginator.js